import { Ribbon, RibbonColor } from "@shared/ui.primitives";
import React from "react";

export type Props = {
  /** CommerceTools ribbon key that maps to a design system colour */
  ribbonKey: string;
  /** Display text for the ribbon */
  text: string;
};

/**
 * Mapping from CommerceTools ribbon keys to Design System ribbon colours.
 *
 * Colour associations:
 * - lightPink: Premium/exclusive items (limitedEdition, backInStock, newIn)
 * - lightYellow: Performance/urgency (sellingFast, lowStock, bestSeller)
 * - lightGrey: Availability/status (outOfStock, comingSoon, lastCall, moreStockComingSoon)
 */
const colourMap: { [key: string]: RibbonColor } = {
  limitedEdition: "lightPink",
  backInStock: "lightPink",
  newIn: "lightPink",
  sellingFast: "lightYellow",
  lowStock: "lightYellow",
  outOfStock: "lightGrey",
  moreStockComingSoon: "lightGrey",
  lastCall: "lightGrey",
  comingSoon: "lightGrey",
  bestSeller: "lightYellow"
};

/**
 * ProductRibbon component that maps CommerceTools ribbon keys to Design System ribbon colours.
 *
 * Used in:
 * - PDP TopBlock component for product page ribbons
 * - VerticalProductCard component for product card ribbons
 *
 * Maps CommerceTools ribbon identifiers to standardized design system colours
 * based on business rules and visual hierarchy.
 *
 * @param ribbonKey - CommerceTools ribbon identifier
 * @param text - Display text for the ribbon
 */
export const ProductRibbon: React.FC<Props> = ({ ribbonKey, text }) => {
  const ribbonColour = colourMap[ribbonKey] || "lightPink";

  return (
    <div data-testid="ribbon-wrapper">
      <Ribbon colour={ribbonColour}>{text}</Ribbon>
    </div>
  );
};
